import React from 'react';
import { Link } from 'react-router-dom';

interface FoundersPromoBadgeProps {
  /** Compact display text (e.g. "47/100" or "Limited") */
  displayText: string;
  /** Whether the count is still loading */
  isLoading?: boolean;
  /** Number of spots remaining (0–100) */
  remainingSpots?: number;
  /** Layout variant */
  variant?: 'compact' | 'card';
}

/**
 * Modern Founders Promo badge/CTA - used in header and mobile nav.
 * Clean gradient border, subtle hover states, no heavy animations.
 */
export function FoundersPromoBadge({
  displayText,
  isLoading = false,
  remainingSpots = 0,
  variant = 'compact',
}: FoundersPromoBadgeProps) {
  const showSpotsLeft = remainingSpots > 0 && remainingSpots <= 10;

  if (variant === 'card') {
    return (
      <div className="mb-3 rounded-xl border-2 border-amber-400/50 bg-gradient-to-br from-amber-500/15 via-amber-600/8 to-yellow-600/15 p-4 shadow-[0_0_24px_-4px_rgba(251,191,36,0.2)] ring-1 ring-amber-400/20">
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-amber-200">
              Founders Promo
            </span>
            <span className="text-lg font-bold text-[#EBFFEE] tabular-nums drop-shadow-sm">
              {isLoading ? '...' : displayText}
            </span>
          </div>
          <p className="text-xs text-[#A9A3A3]">
            Lock in $20/month{showSpotsLeft ? ` · ${remainingSpots} spots left` : ''}
          </p>
          <Link
            to="/terms#founders-promo"
            className="text-xs text-amber-400 hover:text-amber-300 transition-colors underline decoration-amber-500/60 underline-offset-2 w-fit font-medium"
            onClick={(e) => e.stopPropagation()}
          >
            Terms apply
          </Link>
        </div>
      </div>
    );
  }

  // Compact variant for header
  return (
    <Link
      to="/founders-promo"
      className="group block flex-shrink min-w-0 mr-3 md:mr-4"
    >
      <div className="relative overflow-hidden rounded-lg border-2 border-amber-400/40 bg-gradient-to-br from-amber-500/15 via-amber-600/8 to-yellow-600/10 px-3 py-2 md:px-4 md:py-2 shadow-[0_0_16px_-4px_rgba(251,191,36,0.15)] transition-all duration-200 hover:border-amber-400/60 hover:shadow-[0_0_24px_-4px_rgba(251,191,36,0.3)] hover:scale-[1.02]">
        <div className="flex items-center gap-2 font-semibold text-xs md:text-sm whitespace-nowrap">
          {displayText !== 'Founders Promo' && (
            <span className="hidden xl:inline text-amber-200 text-xs">
              Founders Promo:
            </span>
          )}
          <span className="font-bold text-[#EBFFEE] tabular-nums">
            {isLoading ? '...' : displayText}
          </span>
          <span className="hidden 2xl:inline text-[#A9A3A3] text-xs font-normal">
            $20/mo
          </span>
          <span className="hidden 2xl:inline text-[#504A4A]">·</span>
          <span className="text-amber-300 text-xs font-medium">
            Sign Up
          </span>
        </div>
      </div>
    </Link>
  );
}
